/**
 * KRA Photo Journal – Auth Hook
 *
 * Manages JWT session stored in localStorage.
 *
 * Session token is issued by either the legacy PIN flow or the new
 * invite redemption flow (POST /api/auth/redeem-invite). Both return a
 * JWT carrying `sub`, `role`, `staffId`, and optional `admin`. The hook
 * verifies the stored token against /api/auth/me on mount.
 */

import { useState, useEffect, useCallback } from "react";
import { clearGoogleAuth } from "./useGoogleDrive.js";

const TOKEN_KEY = "kra_token";
const API_BASE = (import.meta.env["VITE_API_BASE_URL"] as string | undefined) ?? "/api";

export interface AuthState {
  /** Display name (photographer or device label for invite-based sessions). */
  name: string | null;
  token: string | null;
  /** Normalized role: "admin" | "photographer" | "kimono_staff" | "readonly" | "manager" | "staff" | null */
  role: string | null;
  /** Staff record ID from Airtable, if the invite bound one. */
  staffId: string | null;
  isLoading: boolean;
}

interface AuthMeResponse {
  name: string;
  role?: string;
  staffId?: string;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    name: null,
    token: null,
    role: null,
    staffId: null,
    isLoading: true,
  });

  // Verify stored token on mount
  useEffect(() => {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (!stored) {
      setState({ name: null, token: null, role: null, staffId: null, isLoading: false });
      return;
    }

    fetch(`${API_BASE}/auth/me`, {
      headers: { Authorization: `Bearer ${stored}` },
    })
      .then(async (res) => {
        if (res.ok) {
          const data = (await res.json()) as AuthMeResponse;
          setState({
            name: data.name,
            token: stored,
            role: data.role ?? null,
            staffId: data.staffId ?? null,
            isLoading: false,
          });
        } else {
          localStorage.removeItem(TOKEN_KEY);
          setState({ name: null, token: null, role: null, staffId: null, isLoading: false });
        }
      })
      .catch(() => {
        // Network error — fail closed so we never render the app with a
        // stale token the server has already revoked.
        setState({ name: null, token: null, role: null, staffId: null, isLoading: false });
        localStorage.removeItem(TOKEN_KEY);
      });
  }, []);

  const login = useCallback(
    (token: string, name: string, extras?: { role?: string | null; staffId?: string | null }) => {
      localStorage.setItem(TOKEN_KEY, token);
      setState({
        name,
        token,
        role: extras?.role ?? null,
        staffId: extras?.staffId ?? null,
        isLoading: false,
      });
    },
    []
  );

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    clearGoogleAuth();
    setState({ name: null, token: null, role: null, staffId: null, isLoading: false });
  }, []);

  return { ...state, login, logout };
}

export type UseAuthReturn = ReturnType<typeof useAuth>;
